import { Injectable } from '@angular/core';

import { BetType } from '@bpos/common';
import { OverAskOfferDetails } from '@bpos/common/bet-placement';
import { BetslipConfig, EpcotConfig, SportsUserConfig } from '@frontend/sports/common/client-config-data-access';
import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { ClientConfigService } from '@frontend/vanilla/core';
import { OverlayHandler, OverlayService } from '@frontend/vanilla/features/overlay';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { MyBetsSummaryService } from 'packages/sports/web/app/src/my-bets-base/my-bets-summary.service';
import { PopupManager } from 'packages/sports/web/app/src/popup/popup-manager.service';
import { concatMap, filter, map, mergeMap, tap } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { isLinear } from '../../../base/utils/linear-utils';
import {
    IFailedSlipResult,
    IPlacedBetslipResult,
    ISucceededPartialSlipResult,
    ISucceededSlipResult,
    SlipResultStatus,
} from '../../betplacement/models';
import { isFailedSlip, isSucceededNonPartialSlip } from '../../betplacement/utils';
import { OverAskService } from '../../over-ask/services/over-ask.service';
import BetslipPickActions from '../../picks/actions';
import { getFailedSlipErrors } from '../../result/utils/placed-slip.utils';
import { singleBetPicksSelector } from '../../single-bet/selectors';
import { BetslipErrorBetslipUpdaterService } from '../../validation/services/updaters/betslip-error-betslip-updater.service';
import { isPickStatusLocked, isPickStatusOddsChanged } from '../../validation/services/utils/betslip-errors-utils';
import { getStakeLimitErrorInfo } from '../../validation/services/utils/stake-limit-errors-info-utils';
import { QuickBetActions } from '../quick-bet-actions';
import { quickBetStateSelector } from '../quick-bet.selectors';
import { IQuickBetPlaceResultState, IQuickBetState } from '../quick-bet.state';
import { QuickBetModalDialogService } from '../services/quick-bet-modal-dialog.service';
import { QuickBetPlacementResultProcessorService } from '../services/quick-bet-placement-result-processor.service';
import { QuickBetUtils } from '../services/quick-bet-utils.service';

@Injectable()
export class QuickBetPlaceEffects {
    // private isPlacingInProgress: boolean;

    private overlayHandler: OverlayHandler;

    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private dialogService: QuickBetModalDialogService,
        private overlayService: OverlayService,
        private betslipConfig: BetslipConfig,
        private quickBetUtils: QuickBetUtils,
        private sportsUserConfig: SportsUserConfig,
        private clientConfig: ClientConfigService,
        private popupManager: PopupManager,
        private overAskService: OverAskService,
        private betslipErrorBetslipUpdaterService: BetslipErrorBetslipUpdaterService,
        private myBetsSummary: MyBetsSummaryService,
        private placementResultProcessorService: QuickBetPlacementResultProcessorService,
        private epcotConfig: EpcotConfig,
    ) {
        // this.dispatcher
        // 	.on(BetslipEvents.BetslipStateChanged)
        // 	.subscribe((eventData: BetslipEventPayloads.IBetslipStateChanged) => this.onBetslipStateChanged(eventData));

        this.overlayHandler = this.overlayService.getHandler();
    }

    //check if this is used also from drawer
    handleAcceptOddsChangesEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.acceptOddsChanges),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            map(([_, quickBetState]) => {
                const pickIds = quickBetState.pickIds;

                return BetslipPickActions.acceptCurrentPrice({ pickIds, isManuallyAccepted: true });
            }),
        );
    });

    handleInitBetPlacementEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.initBetPlacement),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, betslipState]) => {
                    this.quickBetUtils.tryFreezeScroll();
                    this.overlayHandler.show();
                    // this.isPlacingInProgress = true;
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleFinalizeBetPlacementEffect$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(QuickBetActions.finalizeBetPlacement),
                concatLatestFrom(() => this.store.select(betslipSelector)),
                tap(([action, betslipState]) => {
                    this.overlayHandler.hide();
                    // this.isPlacingInProgress = false;
                }),
                ignoreElementsTyped(),
            );
        },
        { dispatch: false },
    );

    handleFinishPlaceBetEffect$ = createEffect(() => {
        const this_ = this;

        return this.actions$.pipe(
            ofType(QuickBetActions.finishPlaceBet),
            concatLatestFrom(() => this.store.select(betslipSelector)),
            concatLatestFrom(() => this.store.select(singleBetPicksSelector)),
            concatMap(function* ([[action, betslipState], singleBetPicks]): Iterable<Action> {
                yield QuickBetActions.finalizeBetPlacement();

                const slip = action.placeBetResult.slips[0];
                if (action.placeBetResult.slips.length !== 1) {
                    yield QuickBetActions.showTechnicalErrorDialog();

                    return;
                }

                const failedSlips = action.placeBetResult.slips.filter(isFailedSlip);
                const slipErrors = getFailedSlipErrors(failedSlips, isLinear(betslipState), this_.betslipConfig.isSportcastAsComboEnabled);

                if (failedSlips.length > 0) {
                    const overAsk = this_.tryGetOverAskOffer(slip);
                    if (overAsk) {
                        this_.popupManager.openBetslip();
                        const hint = getStakeLimitErrorInfo(slipErrors);
                        const overAskSlipError = hint.betslipStakeHintInfo ||
                            hint.pickStakeHintInfos[0] || {
                                type: '',
                                newStakeHint: 0,
                            };

                        yield this_.overAskService.createStartFlowAction(overAsk.overAskOfferId, action.request.requestId, overAskSlipError);

                        yield QuickBetActions.closeQuickBet({ cleanErrors: true });

                        return;
                    }
                }

                yield QuickBetActions.placeBetResponseErrors(slipErrors);

                const pickIds = betslipState.quickBet.pickIds;
                const betPlacedResult = this_.placementResultProcessorService.getBetPlaceResultState(action.placeBetResult);

                if (betPlacedResult) {
                    let areOddsChanged = false;
                    let isPickLocked = false;
                    if (!betPlacedResult.isSuccessful) {
                        //work needed this is wrong
                        const errors = (<IFailedSlipResult[]>action.placeBetResult.slips)[0].picks[pickIds.toString()]?.errors ?? [];
                        areOddsChanged = errors.some((error) => isPickStatusOddsChanged(error));
                        isPickLocked = errors.some((error) => isPickStatusLocked(error));
                    }

                    const hasOfferChanges = isPickLocked || areOddsChanged;
                    if (hasOfferChanges) {
                        yield QuickBetActions.showPickChangedWarning({
                            payload: {
                                pickLocked: isPickLocked,
                                oddsChanged: areOddsChanged,
                                isAcceptAndPlaceBetEnabled: this_.betslipConfig.isAcceptAndPlaceBetEnabled,
                            },
                        });

                        const updateActions = this_.betslipErrorBetslipUpdaterService.getUpdateEventsFromPlaceBet(action.placeBetResult, []);
                        for (const updateAction of updateActions) {
                            yield updateAction;
                        }

                        return;
                    }

                    this_.myBetsSummary.refresh(true);

                    if (!this_.epcotConfig.isEnabled) {
                        this_.quickBetUtils.tryReleaseScroll();
                    }

                    if (this_.sportsUserConfig.isEligibleForNewCustomerOffer) {
                        this_.clientConfig.reload([SportsUserConfig]);
                    }

                    if (betPlacedResult.isSuccessful) {
                        this_.updateResultSummary(betPlacedResult, action.placeBetResult);

                        if (isSucceededNonPartialSlip(slip)) {
                            const picks = slip.picks;

                            const betDetail = picks[0].participantBetDetails ?? picks[0].optionBetDetails;

                            if (betDetail?.betType === BetType.EachWay) {
                                betPlacedResult.isEachWay = true;
                            }
                        }
                    }

                    yield QuickBetActions.updatePlaceBetResult({ payload: betPlacedResult });

                    return;
                }

                yield QuickBetActions.showTechnicalErrorDialog();

                return;
            }),
            // catchError((error: PlacementError): Action[] => {
            // 	const sportPickBetResult = this.quickBetUtils.getBetPlaceResultState(betslipState.pickState.picks[0], error);
            //
            // 	if (sportPickBetResult && !(error instanceof Error && error.message)) {
            // 		return [QuickBetActions.updatePlaceBetResult({ payload: sportPickBetResult })];
            // 	}
            //
            // 	return [QuickBetActions.showTechnicalErrorDialog()];
            // })
        );
    });

    handleUpdatePlaceBetResultEffect$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.updatePlaceBetResult),
            concatMap((action) => {
                if (!action.payload.isSuccessful) {
                    if (action.payload.errors.length) {
                        return [QuickBetActions.showPlaceBetErrorDialog()];
                    }

                    if (action.payload.warnings.length) {
                        return [QuickBetActions.showPlaceBetWarning()];
                    }

                    return [QuickBetActions.showTechnicalErrorDialog()];
                }

                return [QuickBetActions.openConfirmation()];
            }),
        );
    });

    handleTechnicalErrorDialogEffect$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(QuickBetActions.showTechnicalErrorDialog),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            mergeMap(function* ([_, state]: [Action, IQuickBetState]): Iterable<Action> {
                effect.dialogService.openTechnicalErrorDialog();

                yield QuickBetActions.closeQuickBet({ cleanErrors: true });
            }),
        );
    });

    handlePlaceBetErrorDialogEffect$ = createEffect(() => {
        const effect = this;

        return this.actions$.pipe(
            ofType(QuickBetActions.showPlaceBetErrorDialog),
            concatLatestFrom(() => this.store.select(quickBetStateSelector)),
            mergeMap(function* ([_, state]: [Action, IQuickBetState]): Iterable<Action> {
                effect.dialogService.openPlaceBetErrorDialog();

                yield QuickBetActions.closeQuickBet({ cleanErrors: true });
            }),
        );
    });

    handleStakeAutoAdjustment$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(QuickBetActions.placeBetResponseErrors),
            map(getStakeLimitErrorInfo),
            filter(({ betslipStakeHintInfo, pickStakeHintInfos }) => !!betslipStakeHintInfo || pickStakeHintInfos.length > 0),
            map(({ betslipStakeHintInfo, pickStakeHintInfos }) => {
                const { newStakeHint } = betslipStakeHintInfo || pickStakeHintInfos[0];

                return QuickBetActions.changeStake({ stake: newStakeHint.toString(), silent: true });
            }),
        );
    });

    // private onBetslipStateChanged(eventData: BetslipEventPayloads.IBetslipStateChanged): void {
    // 	if (!this.isPlacingInProgress) {
    // 		return;
    // 	}
    // 	if (eventData.state.oddsChanged) {
    // 		this.areOddsChanged = true;
    //
    // 		return;
    // 	}
    // 	if (eventData.state.pickLocked) {
    // 		this.isPickLocked = true;
    //
    // 		return;
    // 	}
    // }

    private tryGetOverAskOffer(betslip: ISucceededSlipResult | ISucceededPartialSlipResult | IFailedSlipResult): OverAskOfferDetails | null {
        if (betslip.status === SlipResultStatus.Fail && betslip.overAskOffer != null) {
            return betslip.overAskOffer;
        }

        return null;
    }

    updateResultSummary(quickBetPlaceResultState: IQuickBetPlaceResultState, placementResult: IPlacedBetslipResult): void {
        const succeededSlips = placementResult.slips.filter(isSucceededNonPartialSlip);
        const totalPossibleWinningsGross = succeededSlips.reduce((aggr, current) => {
            return current.possibleWinnings ? aggr + current.possibleWinnings.gross : aggr;
        }, 0);
        const totalPossibleWinningsNet = succeededSlips.reduce((aggr, current) => {
            return current.possibleWinnings ? aggr + current.possibleWinnings.net : aggr;
        }, 0);
        const totalStake = succeededSlips.reduce((aggr, current) => {
            if (current.totalStake) return aggr + current.totalStake;
            else return 0;
        }, 0);
        const betCounts = succeededSlips.reduce((aggr, current) => {
            // Dividing total stake on stake will produce the bet number in the slip.
            if (current.stake && current.totalStake) return aggr + current.totalStake / current.stake;
            else return 0;
        }, 0);
        quickBetPlaceResultState.summary = {
            currency: succeededSlips[0]?.currency,
            numberOfBets: betCounts,
            possibleWinningsGross: totalPossibleWinningsGross,
            possibleWinningsNet: totalPossibleWinningsNet,
            stake: totalStake,
        };
    }
}
